<template>
  <div class="list-page-container">
    <!-- 顶部统计与批量操作区 -->
    <el-row :gutter="16" class="stat-cards-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card points-total-card" shadow="hover" v-loading="statsLoading">
          <div class="stat-card-body">
            <div class="stat-icon-wrapper"><i class="el-icon-coin"></i></div>
            <div class="stat-info">
              <div class="stat-label">系统积分总量</div>
              <div class="stat-value">{{ formatNumber(statistics.totalPoints) }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card active-user-card" shadow="hover" v-loading="statsLoading">
          <div class="stat-card-body">
            <div class="stat-icon-wrapper"><i class="el-icon-user"></i></div>
            <div class="stat-info">
              <div class="stat-label">活跃用户数<span class="stat-sub">近{{ statistics.activeDays || 30 }}天</span></div>
              <div class="stat-value">{{ formatNumber(statistics.activeUserCount) }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="24" :md="12">
        <el-card class="stat-card batch-card" shadow="hover">
          <div class="batch-header">
            <i class="el-icon-magic-stick"></i>
            <span>按等级批量发放积分</span>
          </div>
          <div class="batch-form">
            <el-select v-model="batchForm.userLevel" placeholder="选择用户等级" class="batch-level-select" size="small">
              <el-option v-for="item in levelFilterOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input-number v-model="batchForm.points" :min="1" :max="999999" controls-position="right" size="small" placeholder="积分数量" class="batch-points-input"></el-input-number>
            <el-button type="primary" icon="el-icon-position" size="small" @click="openBatchDialog" v-if="hasPermission('sys:points:batch')" :disabled="!batchForm.userLevel">批量发放</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input
              v-model="searchForm.username"
              placeholder="搜索用户名"
              class="filter-input"
              clearable
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-select v-model="searchForm.userLevel" placeholder="用户等级" clearable class="filter-select" @change="handleSearch">
              <i slot="prefix" class="el-input__icon el-icon-medal"></i>
              <el-option v-for="item in levelFilterOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>

          <div class="search-btns">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <el-card class="list-card">
      <el-table
        ref="table"
        v-loading="loading"
        :data="tableData"
        class="custom-table"
        height="100%"
        style="width: 100%"
      >
        <el-table-column prop="username" label="用户名" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="name" label="姓名" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.name || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="points" label="当前积分" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain" type="warning">{{ scope.row.points || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="userLevel" label="用户等级" width="160" align="center">
          <template slot-scope="scope">
            <el-tag
              size="mini"
              :color="getLevelColor(scope.row.userLevel)"
              effect="dark"
              style="color: #fff; border: none;"
            >
              {{ getLevelDisplayText(scope.row.userLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="mobile" label="手机号" width="130" align="center">
          <template slot-scope="scope">
            {{ scope.row.mobile || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createDate" label="注册时间" width="160" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createDate) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" :width="300" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" size="mini" plain icon="el-icon-document" @click="handleViewRecords(scope.row)">积分明细</el-button>
              <el-button type="success" size="mini" plain icon="el-icon-plus" @click="handleAddPoints(scope.row)" v-if="hasPermission('sys:points:add')">增加积分</el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-minus" @click="handleDeductPoints(scope.row)" v-if="hasPermission('sys:points:deduct')">扣除积分</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="page"
          :page-size="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 积分明细对话框 -->
    <el-dialog
      title="积分变动明细"
      :visible.sync="recordDialogVisible"
      :width="recordDialogWidth"
      :close-on-click-modal="false"
      append-to-body
      custom-class="points-record-dialog"
    >
      <div class="points-record-header">
        <div class="record-user-block">
          <div class="record-user-avatar">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="record-user-meta">
            <div class="record-user-name">
              <strong>{{ currentUser ? currentUser.username : '-' }}</strong>
              <span class="record-user-id">ID: {{ currentUser ? currentUser.userid : '-' }}</span>
            </div>
            <div class="record-user-sub">积分变动历史记录</div>
          </div>
        </div>
        <div class="record-points-badge">
          <i class="el-icon-coin"></i>
          <div class="record-points-text">
            <span class="label">当前积分</span>
            <span class="value">{{ currentUser ? (currentUser.points || 0) : 0 }}</span>
          </div>
        </div>
      </div>

      <div class="points-record-table-wrapper">
        <el-table
          :data="recordData"
          v-loading="recordLoading"
          size="small"
          max-height="380"
          style="width: 100%"
          :header-cell-style="{ background: '#f8fafc', color: '#475569', fontWeight: 600 }"
          class="points-record-table"
        >
          <el-table-column prop="operationTime" label="变动时间" width="155" align="center">
            <template slot-scope="scope">
              <span class="cell-time">{{ formatDate(scope.row.operationTime) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="changeType" label="类型" width="90" align="center">
            <template slot-scope="scope">
              <span :class="['change-type-badge', scope.row.changeType === 'EARN' ? 'is-earn' : 'is-spend']">
                <i :class="scope.row.changeType === 'EARN' ? 'el-icon-top' : 'el-icon-bottom'"></i>
                {{ scope.row.changeType === 'EARN' ? '增加' : '扣除' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="pointsAmount" label="变动数值" width="110" align="center">
            <template slot-scope="scope">
              <span :class="['points-amount', scope.row.changeType === 'EARN' ? 'is-positive' : 'is-negative']">
                {{ scope.row.changeType === 'EARN' ? '+' : '-' }}{{ scope.row.pointsAmount }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="pointsBefore" label="变动前" width="80" align="center">
            <template slot-scope="scope">
              <span class="cell-plain">{{ scope.row.pointsBefore }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="pointsAfter" label="变动后" width="80" align="center">
            <template slot-scope="scope">
              <span class="cell-plain">{{ scope.row.pointsAfter }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="sourceType" label="来源" width="120" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain" type="info">{{ getSourceLabel(scope.row.sourceType) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="cell-remark">{{ scope.row.remark || '-' }}</span>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="!recordLoading && recordData.length === 0" class="points-record-empty">
          <i class="el-icon-document"></i>
          <p>暂无积分变动记录</p>
        </div>
      </div>

      <div class="points-record-pagination">
        <el-pagination
          :current-page="recordPage"
          :page-size="recordLimit"
          :total="recordTotal"
          layout="total, prev, pager, next"
          @current-change="handleRecordPageChange"
        ></el-pagination>
      </div>
      <div slot="footer" class="points-record-footer">
        <el-button type="primary" plain icon="el-icon-circle-close" @click="recordDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 增加积分对话框 -->
    <el-dialog title="手动增加积分" :visible.sync="addDialogVisible" width="500px" :close-on-click-modal="false">
      <el-form :model="addForm" label-width="100px" :rules="addFormRules" ref="addForm">
        <el-form-item label="用户名">
          <el-input :value="currentUser ? currentUser.username + ' (ID: ' + currentUser.userid + ')' : ''" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前积分">
          <el-input :value="currentUser ? (currentUser.points || 0) : 0" disabled></el-input>
        </el-form-item>
        <el-form-item label="增加积分" prop="points">
          <el-input-number v-model="addForm.points" :min="1" :max="999999" controls-position="right" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="操作理由" prop="remark">
          <el-input type="textarea" v-model="addForm.remark" :rows="3" placeholder="请输入增加积分的理由" maxlength="200" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitAddPoints">确认增加</el-button>
      </div>
    </el-dialog>

    <!-- 扣除积分对话框 -->
    <el-dialog title="手动扣除积分" :visible.sync="deductDialogVisible" width="500px" :close-on-click-modal="false">
      <el-form :model="deductForm" label-width="100px" :rules="deductFormRules" ref="deductForm">
        <el-form-item label="用户名">
          <el-input :value="currentUser ? currentUser.username + ' (ID: ' + currentUser.userid + ')' : ''" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前积分">
          <el-input :value="currentUser ? (currentUser.points || 0) : 0" disabled></el-input>
        </el-form-item>
        <el-form-item label="扣除积分" prop="points">
          <el-input-number v-model="deductForm.points" :min="1" :max="currentUser ? (currentUser.points || 0) : 0" controls-position="right" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="操作理由" prop="remark">
          <el-input type="textarea" v-model="deductForm.remark" :rows="3" placeholder="请输入扣除积分的理由" maxlength="200" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="deductDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="submitLoading" @click="submitDeductPoints">确认扣除</el-button>
      </div>
    </el-dialog>

    <!-- 批量发放积分确认对话框 -->
    <el-dialog title="批量发放积分确认" :visible.sync="batchDialogVisible" width="480px" :close-on-click-modal="false">
      <div class="batch-confirm-summary">
        <i class="el-icon-warning-outline summary-icon"></i>
        <div class="summary-text">
          <p>将对等级 <strong>{{ getLevelDisplayText(batchForm.userLevel) }}</strong> 的所有启用用户</p>
          <p>每人发放 <strong class="highlight-points">{{ batchForm.points || 0 }}</strong> 积分</p>
        </div>
      </div>
      <el-form :model="batchForm" label-width="100px" :rules="batchFormRules" ref="batchForm" style="margin-top: 16px;">
        <el-form-item label="发放原因" prop="remark">
          <el-input type="textarea" v-model="batchForm.remark" :rows="3" placeholder="请输入批量发放积分的原因" maxlength="200" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchLoading" @click="submitBatchAdd">确认发放</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList } from '@/apis/user/user'
import { getPointsRecordPage, manualAddPoints, manualDeductPoints, getPointsStatistics, batchAddPointsByLevel } from '@/apis/sys/points'
import { getLevelOptions } from '@/apis/system/level'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'PointsManagement',
  data() {
    return {
      loading: false,
      tableData: [],
      page: 1,
      limit: 10,
      total: 0,
      searchForm: {
        username: '',
        userLevel: ''
      },
      levelOptions: [],
      // 统计数据
      statsLoading: false,
      statistics: {
        totalPoints: 0,
        activeUserCount: 0,
        activeDays: 30
      },
      // 积分明细
      recordDialogVisible: false,
      recordLoading: false,
      recordData: [],
      recordPage: 1,
      recordLimit: 10,
      recordTotal: 0,
      currentUser: null,
      // 增加积分
      addDialogVisible: false,
      addForm: { points: 1, remark: '' },
      addFormRules: {
        points: [{ required: true, message: '请输入积分数量', trigger: 'blur' }],
        remark: [{ required: true, message: '请输入操作理由', trigger: 'blur' }]
      },
      // 扣除积分
      deductDialogVisible: false,
      deductForm: { points: 1, remark: '' },
      deductFormRules: {
        points: [{ required: true, message: '请输入积分数量', trigger: 'blur' }],
        remark: [{ required: true, message: '请输入操作理由', trigger: 'blur' }]
      },
      submitLoading: false,
      // 批量发放
      batchForm: { userLevel: '', points: 100, remark: '' },
      batchFormRules: {
        remark: [{ required: true, message: '请输入发放原因', trigger: 'blur' }]
      },
      batchDialogVisible: false,
      batchLoading: false,
      // 来源类型映射
      sourceTypeMap: {
        'NFC_BIND': 'NFC刷卡绑定',
        'EXCHANGE': '权益兑换',
        'MANUAL_ADD': '手动增加',
        'MANUAL_DEDUCT': '手动扣除',
        'BATCH_ADD': '批量发放',
        'EXCHANGE_REFUND': '兑换退款',
        'EXPIRE': '积分过期'
      }
    }
  },
  computed: {
    levelFilterOptions() {
      return (this.levelOptions || []).map(item => ({
        label: item.levelCode + ' ' + item.levelName,
        value: item.levelCode
      }))
    },
    recordDialogWidth() {
      const w = window.innerWidth
      if (w < 768) return '95%'
      if (w < 1200) return '85%'
      return '880px'
    }
  },
  created() {
    this.loadData()
    this.fetchLevelOptions()
    this.loadStatistics()
  },
  methods: {
    hasPermission,
    loadData() {
      this.loading = true
      const params = {
        page: this.page,
        limit: this.limit,
        ...this.searchForm
      }
      getUserList(params).then(res => {
        this.tableData = res?.list || []
        this.total = res?.total || 0
      }).catch(e => {
        console.error('加载用户积分列表失败:', e)
      }).finally(() => {
        this.loading = false
      })
    },
    fetchLevelOptions() {
      getLevelOptions().then(res => {
        this.levelOptions = res || []
      }).catch(() => {})
    },
    loadStatistics() {
      this.statsLoading = true
      getPointsStatistics(30).then(res => {
        this.statistics = {
          totalPoints: res?.totalPoints || 0,
          activeUserCount: res?.activeUserCount || 0,
          activeDays: res?.activeDays || 30
        }
      }).catch(e => {
        console.error('加载积分统计失败:', e)
      }).finally(() => {
        this.statsLoading = false
      })
    },
    getLevelColor(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item && item.levelColor ? item.levelColor : '#909399'
    },
    getLevelDisplayText(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item ? item.levelCode + ' ' + item.levelName : (level || 'V0')
    },
    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      return Number(num).toLocaleString('zh-CN')
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    handleReset() {
      this.searchForm = { username: '', userLevel: '' }
      this.page = 1
      this.loadData()
    },
    handleSizeChange(size) {
      this.limit = size
      this.page = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.page = page
      this.loadData()
    },
    handleViewRecords(row) {
      this.currentUser = row
      this.recordDialogVisible = true
      this.recordPage = 1
      this.loadRecords()
    },
    loadRecords() {
      if (!this.currentUser) return
      this.recordLoading = true
      const params = {
        page: this.recordPage,
        limit: this.recordLimit,
        userId: this.currentUser.userid
      }
      getPointsRecordPage(params).then(res => {
        this.recordData = res?.list || []
        this.recordTotal = res?.total || 0
      }).catch(e => {
        console.error('加载积分明细失败:', e)
      }).finally(() => {
        this.recordLoading = false
      })
    },
    handleRecordPageChange(page) {
      this.recordPage = page
      this.loadRecords()
    },
    handleAddPoints(row) {
      this.currentUser = row
      this.addForm = { points: 1, remark: '' }
      this.addDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.addForm) this.$refs.addForm.clearValidate()
      })
    },
    handleDeductPoints(row) {
      this.currentUser = row
      this.deductForm = { points: 1, remark: '' }
      this.deductDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.deductForm) this.$refs.deductForm.clearValidate()
      })
    },
    submitAddPoints() {
      this.$refs.addForm.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        manualAddPoints({
          userId: this.currentUser.userid,
          points: this.addForm.points,
          remark: this.addForm.remark
        }).then(() => {
          this.$message.success('积分增加成功')
          this.addDialogVisible = false
          this.loadData()
          this.loadStatistics()
        }).catch(e => {
          this.$message.error(e.message || '积分增加失败')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    submitDeductPoints() {
      this.$refs.deductForm.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        manualDeductPoints({
          userId: this.currentUser.userid,
          points: this.deductForm.points,
          remark: this.deductForm.remark
        }).then(() => {
          this.$message.success('积分扣除成功')
          this.deductDialogVisible = false
          this.loadData()
          this.loadStatistics()
        }).catch(e => {
          this.$message.error(e.message || '积分扣除失败')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    openBatchDialog() {
      if (!this.batchForm.userLevel) {
        this.$message.warning('请先选择用户等级')
        return
      }
      this.batchForm.remark = ''
      this.batchDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.batchForm) this.$refs.batchForm.clearValidate()
      })
    },
    submitBatchAdd() {
      this.$refs.batchForm.validate(valid => {
        if (!valid) return
        this.batchLoading = true
        batchAddPointsByLevel({
          userLevel: this.batchForm.userLevel,
          points: this.batchForm.points,
          remark: this.batchForm.remark
        }).then(res => {
          const count = res?.successCount || 0
          const total = res?.totalPoints || 0
          this.$message.success(`成功为 ${count} 名用户发放积分，共发放 ${total} 积分`)
          this.batchDialogVisible = false
          this.loadData()
          this.loadStatistics()
        }).catch(e => {
          this.$message.error(e.message || '批量发放失败')
        }).finally(() => {
          this.batchLoading = false
        })
      })
    },
    getSourceLabel(sourceType) {
      return this.sourceTypeMap[sourceType] || sourceType || '-'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

/* ---------- 顶部统计卡片 ---------- */
.stat-cards-row {
  flex-shrink: 0;
  margin-bottom: 10px;
  margin-left: 0 !important;
  margin-right: 0 !important;

  .el-col {
    padding-left: 0 !important;
    padding-right: 10px !important;

    &:last-child {
      padding-right: 0 !important;
    }
  }
}

.stat-card {
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  transition: box-shadow 0.3s, transform 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  :deep(.el-card__body) {
    padding: 18px 20px;
  }
}

.stat-card-body {
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 24px;
    color: #fff;
  }
}

.points-total-card {
  .stat-icon-wrapper {
    background: linear-gradient(135deg, #e6a23c, #f7b955);
  }

  .stat-value {
    color: #e6a23c;
  }
}

.active-user-card {
  .stat-icon-wrapper {
    background: linear-gradient(135deg, #409eff, #66b1ff);
  }

  .stat-value {
    color: #409eff;
  }
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.stat-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-sub {
  font-size: 11px;
  color: #909399;
  font-weight: 400;
  background: #f4f4f5;
  padding: 1px 6px;
  border-radius: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.5px;
}

/* ---------- 批量发放卡片 ---------- */
.batch-card {
  :deep(.el-card__body) {
    padding: 14px 20px;
  }
}

.batch-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 12px;

  i {
    font-size: 16px;
    color: #409eff;
  }
}

.batch-form {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  .batch-level-select {
    width: 180px;
  }

  .batch-points-input {
    width: 140px;
  }
}

/* ---------- 批量确认对话框 ---------- */
.batch-confirm-summary {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #ecf5ff;
  border-radius: 8px;
  border: 1px solid #d9ecff;

  .summary-icon {
    font-size: 22px;
    color: #409eff;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .summary-text {
    p {
      margin: 0;
      line-height: 1.8;
      color: #303133;
      font-size: 14px;
    }

    .highlight-points {
      color: #e6a23c;
      font-size: 18px;
    }
  }
}

/* ---------- 积分明细对话框 ---------- */
.points-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, #fff8ed 0%, #fdf4e3 100%);
  border: 1px solid #f5dab1;
  border-radius: 10px;

  .record-user-block {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .record-user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409eff, #66b1ff);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 20px;
      color: #fff;
    }
  }

  .record-user-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .record-user-name {
    font-size: 15px;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 8px;

    .record-user-id {
      color: #909399;
      font-size: 12px;
      font-weight: 400;
    }
  }

  .record-user-sub {
    font-size: 12px;
    color: #909399;
  }

  .record-points-badge {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    padding: 8px 16px;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(230, 162, 60, 0.15);

    > i {
      font-size: 22px;
      color: #e6a23c;
    }

    .record-points-text {
      display: flex;
      flex-direction: column;
      line-height: 1.2;

      .label {
        font-size: 11px;
        color: #909399;
      }

      .value {
        font-size: 20px;
        font-weight: 700;
        color: #e6a23c;
      }
    }
  }
}

.points-record-table-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ebeef5;
}

.points-record-table {
  :deep(.el-table__header-wrapper) th {
    font-size: 13px;
  }

  :deep(.el-table__body) .el-table__row {
    td {
      padding: 10px 0;
      font-size: 13px;
    }

    &:nth-child(even) td {
      background-color: #fafbfc;
    }

    &:hover > td {
      background-color: #f0f7ff !important;
    }
  }
}

.cell-time {
  color: #606266;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

.change-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  i {
    font-size: 12px;
  }

  &.is-earn {
    color: #67c23a;
    background: #f0f9eb;
  }

  &.is-spend {
    color: #f56c6c;
    background: #fef0f0;
  }
}

.points-amount {
  font-size: 15px;
  font-weight: 700;

  &.is-positive {
    color: #67c23a;
  }

  &.is-negative {
    color: #f56c6c;
  }
}

.cell-plain {
  color: #606266;
  font-size: 13px;
}

.cell-remark {
  color: #909399;
  font-size: 12px;
}

.points-record-empty {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;

  i {
    font-size: 36px;
    margin-bottom: 8px;
    display: block;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.points-record-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  :deep(.el-pagination) {
    .btn-prev, .btn-next, .el-pager li {
      background-color: #f8fafc !important;
      border-radius: 4px;
      border: 1px solid #f1f5f9;
      margin: 0 2px;

      &.active {
        background-color: #409eff !important;
        color: #fff !important;
        border-color: #409eff;
      }
    }
  }
}

.points-record-footer {
  text-align: center;

  .el-button {
    min-width: 100px;
    border-radius: 20px;
  }
}

@media screen and (max-width: 768px) {
  .points-record-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    .record-points-badge {
      align-self: flex-end;
    }
  }
}

/* ---------- 响应式 ---------- */
@media screen and (max-width: 768px) {
  .stat-cards-row {
    .el-col {
      padding-right: 0 !important;
      margin-bottom: 10px;
    }
  }

  .batch-form {
    .batch-level-select,
    .batch-points-input {
      width: 100%;
    }
  }
}
</style>
